import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import EmptySectionLayout from './EmptySectionLayout';
import ManageStaffModal, {
    type StaffFormData,
    type StaffServiceOption,
} from '../../../../components/ManageStaffModal/ManageStaffModal';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import ScheduleTab from '../../../../components/ScheduleTab/ScheduleTab';
import ConfirmBookingConflictDialog from '../../../../components/ConfirmBookingConflictDialog/ConfirmBookingConflictDialog';
import BookingConflictBlockBanner from '../../../../components/BookingConflictBlockBanner/BookingConflictBlockBanner';
import { useStaff, type StaffReassignmentConflictDetails } from '../../../../hooks/useStaff';
import { useServices } from '../../../../hooks/useServices';
import { useBranches } from '../../../../hooks/useBranches';
// AB#1842 (PBI-F) — Manage Staff modal needs the host business's
// ServiceType so it can grey out the incompatible gender chip + pre-
// select the legal one in restrictive (WomenOnly / MenOnly) salons.
import { useBusinessDetailsQuery } from '../../../../hooks/useBusinessDetails';
import QuickBookLauncher from '../../../../components/QuickBookModal/QuickBookLauncher';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useToast } from '../../../../contexts/ToastContext';
import { queryKeys } from '../../../../lib/queryKeys';
import { resolveImageUrl } from '../../../../utils/imageUrl';
import { normalizeApiError } from '../../../../types/api-errors';
// AB#1920 (WS-1b.1) — DUAL-RECOGNITION. The BE is flipping booking error
// literals (STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS, STAFF_GENDER_MISMATCHES_SALON,
// …) to canonical registry codes (PR-J0). `normalizeRejectionCode` folds a
// canonical code back onto its legacy spelling so the strict-equality branches
// below recognise BOTH spellings and behave identically. Legacy passes through
// unchanged (additive — no removal). Folding is applied ONLY at these
// behavioural checks; `normalizeApiError().code` is left raw so the generic
// i18n toast (`errors:<CANONICAL_CODE>`) still resolves the canonical key.
import { normalizeRejectionCode } from '../../../../components/QuickBookModal/rejectionMessages';
import type {
    BusinessStaffDto,
    BranchDto,
    PendingInvitationDto,
    ServiceDetailsResponse,
} from '../../../../types/api-responses';
import type { CreateStaffRecordRequest, InviteStaffRecordRequest, UpdateStaffResult } from '../../../../services/api';
import { futureBookingsCountOf } from '../../../../services/api';
// AB#1904 (W-1) — Staff cap upgrade card (sibling of BranchesSection's
// upgradeCard). Renders when the owner is at their plan's staff cap.
import StaffCapUpgradeCard from '../../../../components/StaffCapUpgradeCard/StaffCapUpgradeCard';
import { useCurrentSubscription } from '../../../../hooks/useCurrentSubscription';
import styles from './Section.module.css';
import s from './ManageStaffSection.module.css';

/**
 * AB#1904 (W-1) — Plan-tier staff caps. Mirror of `BranchesSection`'s
 * `PLAN_BRANCH_CAPS`; the BE is the source of truth (enforced server-side),
 * this drives the FE upgrade-hint card. Update alongside any
 * SubscriptionPlan.MaxStaff seed change.
 */
const PLAN_STAFF_CAPS: Record<string, number> = {
    Starter: 2,
    Growth: 7,
    Pro: 20,
    Enterprise: Infinity,
};

/** Resolve a staff photo URL — relative paths route through resolveImageUrl. */
function resolveStaffPhoto(url: string | null | undefined): string | null {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return resolveImageUrl(url);
}

/** Fill `{name}` placeholders in a translated template. */
function fill(template: string, values: Record<string, string | number>): string {
    return Object.entries(values).reduce(
        (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
        template,
    );
}

/** Human-readable "Expires in X" given an ISO date (localized via t). */
function formatExpiry(iso: string, t: (key: string) => string): string {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return t('ost.invite.expired');
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days >= 1) return fill(t(days === 1 ? 'ost.invite.expiresInDay' : 'ost.invite.expiresInDays'), { count: days });
    const hours = Math.floor(ms / (1000 * 60 * 60));
    if (hours >= 1) return fill(t('ost.invite.expiresInHours'), { count: hours });
    return t('ost.invite.expiresSoon');
}

/**
 * CR-2026-05-15-v4 — Manage Staff sub-section (My Business shell).
 *
 * Real list + Add/Edit modal mirroring the canonical Services / Branches
 * pattern. Replaces the AB#1675 placeholder empty-state. Wires the
 * capability-link feature (CR-2026-05-15-v3) onto its natural surface:
 * setting which services a staff can perform is part of adding/editing
 * the staff, not a separate trip to a deprecated /employee page.
 *
 * Reuses the live `salon-owner/staff/*` endpoints via `useStaff` and the
 * `salon-owner/staff/{id}/services` capability endpoint added in
 * CR-2026-05-15-v3 (BE PR !805).
 *
 * Tabs visible from day one (matches the founder-approved mockup):
 *   - Staff list      (this view)
 *   - Schedule        (placeholder, day-2)
 *   - Commission      (placeholder, day-2)
 *   - Roles           (placeholder, day-2)
 *   - Inactive        (filter to !isActive)
 *
 * Not in scope here, follow-up PBIs:
 *   - Schedule / Commission / Roles tabs (placeholders only)
 *   - i18n key extraction (strings hardcoded; matches sibling My Business
 *     sections per BranchesSection.tsx:47-49 documented batch-deferral)
 *   - Bulk-invite via CSV
 *   - Per-branch capability mapping
 */

export interface StaffRow extends BusinessStaffDto {
    initials: string;
}

function toRow(dto: BusinessStaffDto): StaffRow {
    const initials = (dto.fullName ?? '')
        .trim()
        .split(/\s+/)
        .map(part => part[0] || '')
        .join('')
        .slice(0, 2)
        .toUpperCase();
    return { ...dto, initials: initials || '?' };
}

/**
 * AB#1861 — Parse BusinessStaffDto.gender at the row→form adapter boundary.
 *
 * BE serializes the `Gender` enum as a STRING (`"Male"` / `"Female"`) via the
 * global `JsonStringEnumConverter` cemented in PBI-B / AB#1840 on 2026-06-01.
 * The TypeScript surface still declares `gender?: number` (compile-time only),
 * so runtime sees a string while the modal's chip-selected check compares to
 * `STAFF_GENDER_MALE` / `STAFF_GENDER_FEMALE` ints — the comparison silently
 * returns false and the chip renders unselected after every Save+Reopen.
 *
 * We normalize back to int|null here so the rest of the modal (Zod schema,
 * chip-selected check, BE request shaping) keeps working unchanged. The
 * broader enum-converter audit is tracked under AB#1894.
 */
/**
 * Batch B (AB#2176) — staff identity is separate from a login. Adding a staff member
 * creates a bookable staff RECORD (no user account) and returns its real staff id; a
 * portal invitation is optional and only sent when both email and phone were entered.
 */
export interface StaffRecordDeps {
    createStaffRecord: (req: CreateStaffRecordRequest) => Promise<number>;
    inviteStaffRecord: (staffId: number, req: InviteStaffRecordRequest) => Promise<number>;
    showToast: (message: string, type: 'success' | 'error') => void;
    t: (key: string) => string;
}

/** Sends the optional portal invitation; a failure is reported but never undoes the record. */
export async function inviteIfRequested(deps: StaffRecordDeps, staffId: number, form: StaffFormData): Promise<void> {
    const email = form.email.trim();
    const phoneNumber = form.phoneNumber.trim();
    if (!email || !phoneNumber) return;
    try {
        await deps.inviteStaffRecord(staffId, { email, phoneNumber });
        deps.showToast(deps.t('staff.toast.invited').replace('{email}', email), 'success');
    } catch (inviteErr) {
        const reason = inviteErr instanceof Error ? inviteErr.message : '';
        deps.showToast(deps.t('staff.toast.inviteFailed').replace('{reason}', reason), 'error');
    }
}

/** Staff role values on the wire (1 = Staff Manager, 2 = Staff). */
export const STAFF_ROLE_STAFF = 2 as const;

/**
 * S1 (AB#2204, SG-11) — the owner is told when a deactivated staff member still
 * has upcoming bookings (they stay assigned; the owner reassigns or cancels them).
 * Returns the warning, or null when there is nothing to say.
 */
export function deactivationWarning(
    t: (key: string) => string,
    name: string,
    wasActive: boolean,
    nowActive: boolean,
    result: UpdateStaffResult,
): string | null {
    if (!wasActive || nowActive) return null;
    const count = futureBookingsCountOf(result);
    if (count <= 0) return null;
    const template = t(count === 1 ? 'stf.deactivate.futureBookingsOne' : 'stf.deactivate.futureBookingsMany');
    return template.replace('{name}', `\u2068${name}\u2069`).replace('{count}', String(count));
}

/** Creates the staff record, then (optionally) invites it. Returns the REAL staff id. */
export async function addStaffRecord(
    deps: StaffRecordDeps,
    form: StaffFormData,
    gender: number | undefined,
): Promise<number> {
    const staffId = await deps.createStaffRecord({
        fullName: form.fullName,
        gender: gender as CreateStaffRecordRequest['gender'],
        position: form.position,
        isActive: form.isActive,
        // S1 (AB#2204, pilot) — new team members are always Staff (no Staff Manager).
        role: STAFF_ROLE_STAFF,
    });
    deps.showToast(deps.t('staff.toast.added'), 'success');
    await inviteIfRequested(deps, staffId, form);
    return staffId;
}

export function toFormFromRow(row: StaffRow): StaffFormData {
    // Runtime can be string ("Male"/"Female") OR number (1/2) OR nullish.
    // The cast through unknown is intentional — the static type is a lie
    // (`number | undefined`) but runtime sees the post-PBI-B string shape.
    const genderRaw = row.gender as unknown;
    const gender: number | null =
        typeof genderRaw === 'string'
            ? (genderRaw === 'Male'
                ? 1
                : genderRaw === 'Female'
                    ? 2
                    : null)
            : typeof genderRaw === 'number'
                ? genderRaw
                : null;
    return {
        id: row.id,
        fullName: row.fullName ?? '',
        email: row.email ?? '',
        phoneNumber: row.phoneNumber ?? '',
        // AB#1845 (PBI-E) — preserve null when the BE row genuinely has
        // no gender (legacy pre-PBI-E staff). The modal's Zod gate forces
        // the owner to pick one before saving so we don't silently coerce
        // a default the way we used to (`?? 2`).
        // AB#1861 — see helper above; `gender` is int|null at this point.
        gender,
        position: row.position ?? '',
        role: row.role ?? 2,
        isActive: row.isActive,
        schedules: row.schedules,
        photoUrl: row.photoUrl,
        hasLogin: row.hasLogin ?? true,
        invitationPending: row.invitationPending ?? false,
        // branchIds carried through for completeness even though the modal
        // gets them via its own initialBranchIds prop. Useful if future
        // code reads form.branchIds directly.
    } as StaffFormData;
}

type TabKey = 'list' | 'schedule' | 'inactive';

// O3 (SO-14) — unfinished tabs (Commission, Roles) are not rendered at all;
// re-add them here when those features ship.
const TABS: Array<{ key: TabKey; labelKey: string }> = [
    { key: 'list', labelKey: 'ost.tab.list' },
    { key: 'schedule', labelKey: 'ost.tab.schedule' }, // AB#1734 follow-up — wired to ScheduleTab
    { key: 'inactive', labelKey: 'ost.tab.inactive' },
];

const ManageStaffSection: React.FC = () => {
    const { showToast } = useToast();
    const { t } = useLanguage();
    const queryClient = useQueryClient();
    const { canManageStaff, canDeleteStaff } = useAuthContext();
    const {
        getBusinessStaff,
        createStaffRecord,
        inviteStaffRecord,
        updateStaff,
        deleteStaff,
        setStaffServices,
        setStaffBranches,
        getPendingInvitations,
        resendInvitation,
        revokeInvitation,
        setStaffPhoto,
        setStaffSchedule,
        getStaffSchedule,
        loading,
    } = useStaff();
    const { getServices } = useServices({ forOwner: true });
    const { getBranches } = useBranches();
    // AB#1842 (PBI-F) — single-fetch business details (cached 60s by
    // useBusinessDetailsQuery, shared with BusinessDetailsSection). Only
    // the `serviceType` field is consumed here; the modal normalises
    // string / number / null to a sentinel via `normaliseSalonServiceType`.
    const { data: businessDetails } = useBusinessDetailsQuery();
    const salonServiceType = businessDetails?.serviceType ?? null;

    const [rows, setRows] = useState<StaffRow[] | null>(null);
    const [services, setServices] = useState<ServiceDetailsResponse[]>([]);
    const [branches, setBranches] = useState<BranchDto[]>([]);
    const [pending, setPending] = useState<PendingInvitationDto[]>([]);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [tab, setTab] = useState<TabKey>('list');

    // CR-2026-05-16-v1 — filter state. Local (not URL-persisted) for now to
    // match the simpler scope of the staff list; CR-v5 brought URL state to
    // /my-business/services, we can mirror that pattern here later.
    const [filterSearch, setFilterSearch] = useState('');
    const [filterBranchId, setFilterBranchId] = useState<string>('');
    const [filterServiceId, setFilterServiceId] = useState<string>('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAdd, setIsAdd] = useState(true);
    const [editingRow, setEditingRow] = useState<StaffRow | null>(null);
    // AB#1845 (PBI-E) / AB#1842 (PBI-F) — server-side validation error
    // forwarded to the ManageStaffModal so it can render the localized
    // message under the matching field. Cleared on modal close + on each
    // save attempt. AB#1842 extends the supported code list with the new
    // salon-gender hard-block code.
    const [modalExternalError, setModalExternalError] = useState<
        { field: 'gender'; code: 'STAFF_GENDER_REQUIRED' | 'STAFF_GENDER_MISMATCHES_SALON' } | null
    >(null);

    // AB#1734 follow-up — `initialScheduleSlots` state removed.
    // Schedule editing moved to the dedicated Schedule tab. The staff modal
    // no longer needs to fetch / pass schedule data on edit. ScheduleTab
    // handles its own fetch/seed/save lifecycle when the picker changes.

    // Track which pending invitation is currently being resent / revoked so
    // we can disable just that row's actions instead of the whole panel.
    const [busyInvitationId, setBusyInvitationId] = useState<number | null>(null);
    // Track which invitation link was just copied so we can flash a "Copied!"
    // confirmation under that row.
    const [copiedInvitationId, setCopiedInvitationId] = useState<number | null>(null);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deletingRow, setDeletingRow] = useState<StaffRow | null>(null);

    // AB#1862 — booking-conflict guard state.
    //
    // The confirm dialog is shown when SetStaffBranches / SetStaffServices
    // rejects with `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS`. We stash the entire
    // pending save context (form / serviceIds / branchIds / staffId) so we
    // can re-fire the affected mutation with `forceWithActiveBookings: true`
    // when the owner accepts. The dialog renders independent of the modal
    // overlay (its own z-index layer above the staff modal).
    //
    // The block banner is shown when DeleteStaff rejects with the same code.
    // No force override exists for delete (per Faris arch-brief §2.3 carve-
    // out), so we just need the details + staff name to render.
    const [confirmDialog, setConfirmDialog] = useState<{
        details: StaffReassignmentConflictDetails;
        staffId: number;
        staffName: string;
        /** Pending Save context — what we'll re-fire on `Continue anyway`. */
        pendingServiceIds: number[];
        pendingBranchIds: number[];
        /** Inline error surfaced inside the dialog if the force-retry itself fails. */
        inlineError: string | null;
        /** Disable buttons while the force-retry is in flight. */
        busy: boolean;
    } | null>(null);
    const [blockBanner, setBlockBanner] = useState<{
        details: StaffReassignmentConflictDetails;
        staffName: string;
    } | null>(null);

    const invalidateServicesCache = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.services.list() });
        // Batch B (AB#2176) — the submit checklist's staff item reads the staff list query.
        queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });
    }, [queryClient]);

    // Batch B (AB#2176) — one flag for the whole save (create → optional invite →
    // services / branches), so the modal stays busy between the separate requests.
    const [saving, setSaving] = useState(false);

    const loadAll = useCallback(async () => {
        setFetchError(null);
        try {
            // BE caps pageSize at 50; this is the FE's per-request page bound.
            // Day-2: switch to client-side paging when a single salon exceeds 50 staff.
            // Pending invitations + branches are fire-and-forget: if either
            // fails we still render the staff list and surface a softer
            // warning rather than failing the whole page.
            const [staffRes, servicesData, pendingData, branchesRes] = await Promise.all([
                getBusinessStaff({ pageSize: 50 }),
                getServices(),
                getPendingInvitations().catch(() => [] as PendingInvitationDto[]),
                getBranches().catch(() => null),
            ]);
            setRows((staffRes?.staff ?? []).map(toRow));
            setServices(servicesData ?? []);
            setPending(pendingData ?? []);
            setBranches((branchesRes?.branches ?? []).filter(b => b.active));
        } catch (err) {
            const msg = (err as Error)?.message || t('ost.error.load');
            setFetchError(msg);
            if (rows === null) showToast(msg, 'error');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getBusinessStaff, getServices, getPendingInvitations, getBranches, showToast, t]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const serviceOptions = useMemo<StaffServiceOption[]>(
        () => services.map(s => ({
            id: s.id,
            name: s.name,
            isActive: s.isActive,
            categoryName: s.categoryName,
            hasHomeService: s.hasHomeService,
            // AB#1734 follow-up — branch scope for the staff-modal capability
            // picker. Each service belongs to exactly one branch.
            branchId: s.branchId,
        })),
        [services],
    );

    // staffId → serviceIds[] inverted from ServiceDetailsResponse.assignedStaffIds.
    // Avoids needing a new GET endpoint for per-staff capability set.
    const serviceIdsByStaffId = useMemo(() => {
        const map = new Map<number, number[]>();
        for (const svc of services) {
            for (const staffId of svc.assignedStaffIds ?? []) {
                const list = map.get(staffId) ?? [];
                list.push(svc.id);
                map.set(staffId, list);
            }
        }
        return map;
    }, [services]);

    const initialServiceIdsForModal = useMemo(() => {
        if (isAdd || !editingRow?.id) return [];
        return serviceIdsByStaffId.get(editingRow.id) ?? [];
    }, [isAdd, editingRow, serviceIdsByStaffId]);

    // CR-2026-05-16-v2 — branch options + initial selection for the modal.
    const branchOptions = useMemo(
        () => branches.map(b => ({
            id: b.id,
            name: b.name,
            isPrimary: b.primaryHeadQuarter,
        })),
        [branches],
    );

    const initialBranchIdsForModal = useMemo(() => {
        if (isAdd || !editingRow) return [];
        return editingRow.branchIds ?? [];
    }, [isAdd, editingRow]);

    /* ── Modal handlers ─────────────────────────────────────────────── */
    const openAdd = () => {
        if (!canManageStaff) {
            showToast(t('ost.error.noPermissionAdd'), 'error');
            return;
        }
        setIsAdd(true);
        setEditingRow(null);
        setModalExternalError(null);
        setIsModalOpen(true);
    };

    const openEdit = (row: StaffRow) => {
        // AB#1734 follow-up — Schedule moved to its own tab; the staff modal
        // no longer needs an async fetch on open. Identity + capabilities
        // load synchronously from `row` already.
        setIsAdd(false);
        setEditingRow(row);
        setModalExternalError(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (loading) return;
        setIsModalOpen(false);
        setModalExternalError(null);
    };

    const handleSave = async (
        form: StaffFormData,
        serviceIds: number[],
        branchIds: number[],
        // AB#1734 follow-up — schedule slots dropped from the modal's onSave
        // signature. Schedule editing moved to its own tab + Save button.
    ) => {
        // AB#1845 (PBI-E) — clear any stale BE error from a previous attempt
        // every time the owner re-submits. If the BE re-rejects with the
        // same code we re-set it below.
        setModalExternalError(null);
        if (saving) return;
        setSaving(true);
        try {
            // AB#1845 (PBI-E) — modal's Zod gate guarantees a non-null gender
            // by the time we get here. Coerce the union back to number for
            // the BE request shapes (`RegisterStaffRequest.gender: Gender`).
            // The `?? undefined` keeps the field absent if it really is null,
            // letting the BE's STAFF_GENDER_REQUIRED validator fire — which
            // we then surface via `modalExternalError`.
            const genderForBE = form.gender ?? undefined;
            let staffId: number | undefined;
            const recordDeps: StaffRecordDeps = { createStaffRecord, inviteStaffRecord, showToast, t };
            if (isAdd) {
                // Batch B (AB#2176, G-10 / G-11) — the services / branches below are saved
                // against the REAL staff id this returns (they used to be saved against the
                // invitation id).
                staffId = await addStaffRecord(recordDeps, form, genderForBE);
            } else if (form.id != null) {
                staffId = form.id;
                const updateResult = await updateStaff(form.id, {
                    fullName: form.fullName,
                    email: form.email,
                    phoneNumber: form.phoneNumber,
                    gender: genderForBE,
                    position: form.position,
                    isActive: form.isActive,
                    // S1 (AB#2204) — the stored role is sent back unchanged (an existing
                    // Staff Manager is never downgraded silently).
                    role: form.role === 1 ? 1 : 2,
                });
                showToast(t('ost.toast.updated'), 'success');
                const warning = deactivationWarning(
                    t,
                    form.fullName?.trim() || fill(t('ost.staffFallbackName'), { id: form.id }),
                    editingRow?.isActive ?? true,
                    form.isActive,
                    updateResult,
                );
                if (warning) showToast(warning, 'warning');
                // Batch B (AB#2176) — optional portal invitation for a record without a login.
                if (!form.hasLogin && !form.invitationPending) {
                    await inviteIfRequested(recordDeps, form.id, form);
                }
            }

            // Capability set + branch enrollment — idempotent on BE; best-
            // effort, fire independently so one failure doesn't block the
            // other. AB#1734 follow-up: schedule save moved to ScheduleTab.
            //
            // AB#1862 — when either mutation returns 422 +
            // `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS`, open the confirm dialog
            // with the pending mutation context preserved so we can replay
            // the SAME call with `forceWithActiveBookings: true` on owner
            // ack. The other mutation (the one that succeeded) doesn't need
            // a re-fire — the BE is idempotent. We bail out of the rest of
            // the success path so the modal stays open under the dialog
            // (the dialog renders at a higher z-index).
            if (staffId != null) {
                const staffNameForDialog = form.fullName?.trim() || fill(t('ost.staffFallbackName'), { id: staffId });
                try {
                    await setStaffServices(staffId, serviceIds);
                } catch (e) {
                    const mutationErr = e as
                        & Partial<Error>
                        & { code?: string; details?: StaffReassignmentConflictDetails };
                    if (mutationErr.code === 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS' && mutationErr.details) {
                        setConfirmDialog({
                            details: mutationErr.details,
                            staffId,
                            staffName: staffNameForDialog,
                            pendingServiceIds: serviceIds,
                            pendingBranchIds: branchIds,
                            inlineError: null,
                            busy: false,
                        });
                        return; // keep modal + dialog open; owner decides next.
                    }
                    const msg = e instanceof Error ? e.message : t('ost.error.saveServices');
                    showToast(msg, 'error');
                }
                try {
                    await setStaffBranches(staffId, branchIds);
                } catch (e) {
                    const mutationErr = e as
                        & Partial<Error>
                        & { code?: string; details?: StaffReassignmentConflictDetails };
                    if (mutationErr.code === 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS' && mutationErr.details) {
                        setConfirmDialog({
                            details: mutationErr.details,
                            staffId,
                            staffName: staffNameForDialog,
                            pendingServiceIds: serviceIds,
                            pendingBranchIds: branchIds,
                            inlineError: null,
                            busy: false,
                        });
                        return;
                    }
                    const msg = e instanceof Error ? e.message : t('ost.error.saveBranches');
                    showToast(msg, 'error');
                }
            }

            setIsModalOpen(false);
            invalidateServicesCache();
            await loadAll();
        } catch (err) {
            // AB#1845 (PBI-E) / AB#1842 (PBI-F) — surface field-level BE
            // codes inside the modal instead of the generic toast so the
            // owner sees the message right next to the gender chips.
            // `useStaff.registerStaff/updateStaff` wraps axios errors and
            // re-throws an Error with `.code` preserved (the api.ts
            // interceptor copies `response.data.code` onto error.code).
            // We also defensively peek at the original axios shape in case
            // a future hook bypasses the wrapper.
            // AB#1919 (WS-5b) — `code` via the single normalizer (`errorCode
            // ?? code`); the useStaff wrapper already stamps `.code`, and the
            // normalizer also covers the raw axios body defensively.
            const normalizedCode = normalizeApiError(err).code;
            // AB#1920 (WS-1b.1) — fold canonical → legacy so the gender
            // strict-equality checks below recognise both spellings.
            // STAFF_GENDER_MISMATCHES_SALON (legacy) and STAFF.GENDER_MISMATCH
            // (canonical) both resolve here; STAFF_GENDER_REQUIRED has no
            // canonical and passes through unchanged.
            const code =
                normalizedCode !== 'UNKNOWN'
                    ? normalizeRejectionCode(normalizedCode)
                    : undefined;
            if (code === 'STAFF_GENDER_REQUIRED') {
                setModalExternalError({ field: 'gender', code: 'STAFF_GENDER_REQUIRED' });
                showToast(t('staff.errors.genderRequired'), 'error');
                return; // keep the modal open so the owner can fix and retry
            }
            if (code === 'STAFF_GENDER_MISMATCHES_SALON') {
                setModalExternalError({ field: 'gender', code: 'STAFF_GENDER_MISMATCHES_SALON' });
                showToast(t('staff.errors.genderMismatchesSalon'), 'error');
                return; // keep the modal open so the owner can fix and retry
            }
            const msg = (err as Error)?.message || t('ost.error.save');
            showToast(msg, 'error');
        } finally {
            setSaving(false);
        }
    };

    const openDeleteFromModal = () => {
        if (!editingRow) return;
        setDeletingRow(editingRow);
        setIsModalOpen(false);
        setIsDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!deletingRow) return;
        try {
            await deleteStaff(deletingRow.id);
            showToast(t('ost.toast.deleted'), 'success');
            setIsDeleteOpen(false);
            setDeletingRow(null);
            invalidateServicesCache();
            await loadAll();
        } catch (err) {
            // AB#1862 — delete is the only true HARD block per Faris arch-
            // brief §2.3. Surface the structured 422 as the block banner
            // (no force CTA, no override). Close the delete confirm and
            // hand off to the banner so the owner sees the count + earliest
            // date + Calendar deep-link instead of just a generic toast.
            const mutationErr = err as
                & Partial<Error>
                & { code?: string; details?: StaffReassignmentConflictDetails };
            if (mutationErr.code === 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS' && mutationErr.details) {
                setBlockBanner({
                    details: mutationErr.details,
                    staffName: deletingRow.fullName?.trim() || fill(t('ost.staffFallbackName'), { id: deletingRow.id }),
                });
                setIsDeleteOpen(false);
                return;
            }
            const msg = (err as Error)?.message || t('ost.error.delete');
            showToast(msg, 'error');
        }
    };

    /**
     * AB#1862 — Owner pressed "Continue anyway" inside the confirm dialog.
     * Re-fires the SAME mutation that initially threw, this time with
     * `forceWithActiveBookings: true`. On success we close everything and
     * reload; on failure we surface the error inline so the owner can
     * retry without losing the modal context.
     */
    const handleConfirmForce = useCallback(async () => {
        if (!confirmDialog) return;
        const { details, staffId, pendingServiceIds, pendingBranchIds } = confirmDialog;
        setConfirmDialog(prev => prev ? { ...prev, busy: true, inlineError: null } : prev);
        try {
            // Re-fire only the mutation that threw — the handler is encoded
            // in the BE details payload, so we don't need to guess.
            if (details.handler === 'SetStaffServices') {
                await setStaffServices(staffId, pendingServiceIds, true);
                // Branch save was either successful before this or wasn't
                // attempted yet (services threw first). Either way, fire it
                // now without force — if it ALSO conflicts, we surface that
                // as a second dialog turn. Idempotent on BE so re-firing a
                // successful save is a no-op.
                try {
                    await setStaffBranches(staffId, pendingBranchIds);
                } catch (innerErr) {
                    const innerMutationErr = innerErr as
                        & Partial<Error>
                        & { code?: string; details?: StaffReassignmentConflictDetails };
                    if (
                        innerMutationErr.code === 'STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS' &&
                        innerMutationErr.details
                    ) {
                        // Transition the dialog to the branch conflict in
                        // place (preserves the dialog open-state so the
                        // owner doesn't get a flash of nothing).
                        setConfirmDialog(prev => prev ? {
                            ...prev,
                            details: innerMutationErr.details!,
                            busy: false,
                            inlineError: null,
                        } : prev);
                        return;
                    }
                    throw innerErr;
                }
            } else if (details.handler === 'SetStaffBranches') {
                await setStaffBranches(staffId, pendingBranchIds, true);
            }
            // Force-retry succeeded — show the audit toast so the owner
            // has a paper trail that ghost bookings now exist, then close
            // the modal + dialog and refresh.
            const toastTemplate = t('staff.reassign.conflict.forcedToast');
            const toastMsg = toastTemplate.split('{count}').join(String(details.count));
            showToast(toastMsg, 'success');
            setConfirmDialog(null);
            setIsModalOpen(false);
            invalidateServicesCache();
            queryClient.invalidateQueries({ queryKey: queryKeys.staff.list() });
            await loadAll();
        } catch (err) {
            // Non-conflict error on the force-retry — surface inside the
            // dialog so the owner can re-try (e.g. transient network).
            const fallback = t('staff.reassign.conflict.networkRetry');
            const msg = err instanceof Error && err.message ? err.message : fallback;
            setConfirmDialog(prev => prev ? { ...prev, busy: false, inlineError: msg } : prev);
        }
    }, [
        confirmDialog,
        setStaffServices,
        setStaffBranches,
        invalidateServicesCache,
        loadAll,
        queryClient,
        showToast,
        t,
    ]);

    /* ── Filter application ─────────────────────────────────────────── */
    // Client-side filter, mirrors the BE query slot but applied here so
    // re-filtering doesn't re-hit the network. Switch to BE-side filtering
    // when the staff list exceeds the 50-row pageSize cap.
    const filteredRows = useMemo(() => {
        if (!rows) return null;
        const q = filterSearch.trim().toLowerCase();
        const branchIdNum = filterBranchId ? parseInt(filterBranchId, 10) : null;
        const serviceIdNum = filterServiceId ? parseInt(filterServiceId, 10) : null;
        return rows.filter(r => {
            // Tab scope first — Inactive tab shows only inactive staff;
            // the default Staff-list view shows actives (and day-2 placeholder
            // tabs fall through showing actives too).
            if (tab === 'inactive') {
                if (r.isActive) return false;
            } else if (!r.isActive) {
                return false;
            }
            if (q) {
                const matchesText =
                    (r.fullName ?? '').toLowerCase().includes(q) ||
                    (r.email ?? '').toLowerCase().includes(q) ||
                    (r.phoneNumber ?? '').toLowerCase().includes(q) ||
                    (r.position ?? '').toLowerCase().includes(q);
                if (!matchesText) return false;
            }
            if (serviceIdNum != null) {
                const capabilityIds = serviceIdsByStaffId.get(r.id) ?? [];
                // Implicit-all default: staff with empty list is treated as
                // eligible for the service. Owners can narrow later.
                if (capabilityIds.length > 0 && !capabilityIds.includes(serviceIdNum)) return false;
            }
            if (branchIdNum != null) {
                // CR-2026-05-16-v2 — same implicit-all rule for branches:
                // staff with empty branchIds is treated as eligible at any
                // branch.
                const branchLinks = r.branchIds ?? [];
                if (branchLinks.length > 0 && !branchLinks.includes(branchIdNum)) return false;
            }
            return true;
        });
    }, [rows, tab, filterSearch, filterBranchId, filterServiceId, serviceIdsByStaffId]);

    const hasActiveFilters = !!filterSearch.trim() || !!filterBranchId || !!filterServiceId;
    const clearAllFilters = useCallback(() => {
        setFilterSearch('');
        setFilterBranchId('');
        setFilterServiceId('');
    }, []);

    /* ── Pending invitation actions ─────────────────────────────────── */
    const handleResendInvitation = async (id: number, email: string) => {
        if (busyInvitationId != null) return;
        setBusyInvitationId(id);
        try {
            await resendInvitation(id);
            showToast(fill(t('ost.invite.resent'), { email }), 'success');
            const refreshed = await getPendingInvitations().catch(() => null);
            if (refreshed) setPending(refreshed);
        } catch (err) {
            const msg = err instanceof Error ? err.message : t('ost.invite.resendFailed');
            showToast(msg, 'error');
        } finally {
            setBusyInvitationId(null);
        }
    };

    const handleRevokeInvitation = async (id: number, email: string) => {
        if (busyInvitationId != null) return;
        // eslint-disable-next-line no-alert
        const ok = window.confirm(fill(t('ost.invite.revokeConfirm'), { email }));
        if (!ok) return;
        setBusyInvitationId(id);
        try {
            await revokeInvitation(id);
            showToast(t('ost.invite.revoked'), 'success');
            setPending(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            const msg = err instanceof Error ? err.message : t('ost.invite.revokeFailed');
            showToast(msg, 'error');
        } finally {
            setBusyInvitationId(null);
        }
    };

    const handleCopyInvitationLink = async (id: number, link: string) => {
        try {
            await navigator.clipboard.writeText(link);
            setCopiedInvitationId(id);
            showToast(t('ost.invite.linkCopied'), 'success');
            window.setTimeout(() => {
                setCopiedInvitationId(current => (current === id ? null : current));
            }, 2400);
        } catch {
            showToast(t('ost.invite.copyFailed'), 'error');
        }
    };

    /* ── View state ─────────────────────────────────────────────────── */
    const isInitialLoading = loading && rows === null;
    const hasAnyContent = (rows?.length ?? 0) > 0 || pending.length > 0;
    const showEmpty = !isInitialLoading && !fetchError && rows !== null && !hasAnyContent;
    const showList = !isInitialLoading && !fetchError && rows !== null && hasAnyContent;

    const activeCount = useMemo(
        () => (rows ?? []).filter(r => r.isActive).length,
        [rows],
    );
    const inactiveCount = (rows?.length ?? 0) - activeCount;

    /* ── AB#1904 (W-1) — Staff cap upgrade card ──────────────────────── */
    const { data: subscription } = useCurrentSubscription();
    const planName = subscription?.planName ?? 'Starter';
    const staffCap = PLAN_STAFF_CAPS[planName] ?? PLAN_STAFF_CAPS.Starter;
    // Cap counts BOTH active staff AND pending invitations — once accepted
    // they become active, so the BE enforces the cap against this combined
    // total. Stay in sync with the server-enforced gate.
    // Batch B (AB#2176) — an invitation for an existing staff record is not an extra
    // staff member, so rows with a pending invitation are not counted twice.
    const linkedPending = rows?.filter(r => r.invitationPending).length ?? 0;
    const staffTotal = (rows?.length ?? 0) + Math.max(0, (pending?.length ?? 0) - linkedPending);
    const atStaffCap = Number.isFinite(staffCap) && staffTotal >= staffCap;

    /* ── Card rendering helper ──────────────────────────────────────── */
    const renderCard = (row: StaffRow) => {
        const capabilityIds = serviceIdsByStaffId.get(row.id) ?? [];
        const photoSrc = resolveStaffPhoto(row.photoUrl);
        // AB#1845 (PBI-E) — non-blocking banner above the card content for
        // staff rows still missing the gender field. Legacy data only —
        // new invites are gated by the modal's Zod rule + BE validator so
        // this banner is expected to drain to zero shortly after rollout.
        // We treat any falsy gender value (null / undefined / 0) as
        // "missing" rather than just null, because legacy BE rows can
        // surface gender as 0 (default value) before the migration normalises them.
        const isMissingGender = !row.gender;
        return (
            <li key={row.id} className={s.card}>
                {isMissingGender && (
                    <div
                        className={s.genderMissingBanner}
                        role="status"
                        aria-live="polite"
                    >
                        <span className={s.genderMissingIcon} aria-hidden="true">!</span>
                        <span className={s.genderMissingText}>
                            {t('staff.banner.completeGender')}
                        </span>
                        <button
                            type="button"
                            className={s.genderMissingCta}
                            onClick={() => openEdit(row)}
                            aria-label={`${t('staff.banner.completeGender.cta')} — ${row.fullName}`}
                        >
                            {t('staff.banner.completeGender.cta')}
                        </button>
                    </div>
                )}
                <div className={s.avatar} aria-hidden="true">
                    {photoSrc ? (
                        <img src={photoSrc} alt="" className={s.avatarImg} />
                    ) : (
                        row.initials
                    )}
                </div>
                <div className={s.cardMain}>
                    <div className={s.cardNameRow}>
                        <span className={s.cardName}>{row.fullName}</span>
                        <span
                            className={`${s.pill} ${row.isActive ? s.statusActive : s.statusInactive}`}
                        >
                            {row.isActive ? t('staff.status.active') : t('staff.status.inactive')}
                        </span>
                        {/* S2 (AB#2205, SG-09) — no "Manager" pill in the pilot: every team
                            member shows as Staff (the stored role is untouched). */}
                        {row.hasLogin === false && (
                            <span className={`${s.pill} ${s.positionPill}`}>
                                {row.invitationPending ? t('staff.status.invitePending') : t('staff.status.noPortal')}
                            </span>
                        )}
                        {row.position && (
                            <span className={`${s.pill} ${s.positionPill}`}>{row.position}</span>
                        )}
                    </div>
                    <div className={s.cardMeta}>
                        {row.email && <span>{row.email}</span>}
                        {row.email && row.phoneNumber && (
                            <span className={s.cardMetaSep}>·</span>
                        )}
                        {row.phoneNumber && <span>{row.phoneNumber}</span>}
                    </div>
                    <div className={s.capabilityRow}>
                        <span className={s.capabilityLabel}>{t('ost.canPerform')}</span>
                        {capabilityIds.length === 0 ? (
                            <span
                                className={`${s.pill} ${s.capabilityAny}`}
                                title={t('ost.capability.anyHint')}
                            >
                                {t('ost.capability.any')}
                            </span>
                        ) : (
                            <span className={`${s.pill} ${s.capabilityCount}`}>
                                {fill(t(capabilityIds.length === 1 ? 'ost.capability.countOne' : 'ost.capability.count'), { count: capabilityIds.length })}
                            </span>
                        )}
                    </div>
                </div>
                <div className={s.cardActions}>
                    {/* AB#1752 — Staff-first quick-book entry-point in the
                        staff card actions row (mockup §D companion to the
                        Calendar right-click menu). Resolves branch via the
                        staff's branchIds: primary-HQ → first listed → null.
                        Inactive staff don't get the button. */}
                    {row.isActive && (() => {
                        const staffBranchIds = row.branchIds ?? [];
                        const candidate = branches.find(
                            b => b.primaryHeadQuarter && staffBranchIds.includes(b.id),
                        )
                            ?? branches.find(b => staffBranchIds.includes(b.id))
                            ?? branches[0]
                            ?? null;
                        return (
                            <QuickBookLauncher
                                branchId={candidate?.id ?? null}
                                branchName={candidate?.name}
                                lockedStaffId={row.id}
                                lockedStaffName={row.fullName ?? fill(t('ost.staffFallbackName'), { id: row.id })}
                                variant="ghost"
                                label={t('ost.quickBook')}
                            />
                        );
                    })()}
                    <button
                        type="button"
                        className={s.iconBtn}
                        onClick={() => openEdit(row)}
                        aria-label={fill(t('ost.action.editNamed'), { name: row.fullName ?? '' })}
                    >
                        {t('ost.action.edit')}
                    </button>
                    {canDeleteStaff && (
                        <button
                            type="button"
                            className={`${s.iconBtn} ${s.iconBtnDanger}`}
                            onClick={() => { setDeletingRow(row); setIsDeleteOpen(true); }}
                            aria-label={fill(t('ost.action.deleteNamed'), { name: row.fullName ?? '' })}
                        >
                            {t('ost.action.delete')}
                        </button>
                    )}
                </div>
            </li>
        );
    };

    /* ── Render ─────────────────────────────────────────────────────── */
    if (showEmpty) {
        return (
            <>
                <EmptySectionLayout
                    title={t('ost.title')}
                    subtitle={t('ost.subtitle')}
                    tabs={TABS.map(tb => t(tb.labelKey))}
                    illustration="👥"
                    emptyHeadline={t('ost.empty.headline')}
                    emptyDescription={t('ost.empty.description')}
                    primaryCtaLabel={t('ost.empty.cta')}
                    onPrimary={openAdd}
                    benefits={[
                        {
                            icon: '🗓️',
                            title: t('ost.benefit.schedule.title'),
                            description: t('ost.benefit.schedule.desc'),
                        },
                        {
                            icon: '💼',
                            title: t('ost.benefit.services.title'),
                            description: t('ost.benefit.services.desc'),
                        },
                        {
                            icon: '📱',
                            title: t('ost.benefit.app.title'),
                            description: t('ost.benefit.app.desc'),
                        },
                    ]}
                />
                {isModalOpen && (
                    <ManageStaffModal
                        isOpen={isModalOpen}
                        isAdd={isAdd}
                        initial={null}
                        services={serviceOptions}
                        initialServiceIds={[]}
                        branches={branchOptions}
                        initialBranchIds={[]}
                        loading={loading}
                        externalError={modalExternalError}
                        salonServiceType={salonServiceType}
                        onClose={closeModal}
                        onSave={handleSave}
                    />
                )}
            </>
        );
    }

    return (
        <div>
            <header className={styles.header}>
                <h1>{t('ost.title')}</h1>
                <p>{t('ost.subtitle')}</p>
            </header>

            <div className={s.tabs} role="tablist" aria-label={t('ost.tabs.aria')}>
                {TABS.map(tb => (
                    <button
                        key={tb.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === tb.key}
                        className={`${s.tab} ${tab === tb.key ? s.tabActive : ''}`}
                        onClick={() => setTab(tb.key)}
                    >
                        {t(tb.labelKey)}
                    </button>
                ))}
            </div>

            {/* AB#1734 follow-up — Schedule tab content lives in its own
                component now (moved out of the staff edit modal). */}
            {tab === 'schedule' && (
                <ScheduleTab
                    staff={(rows ?? []).map(r => ({
                        id: r.id,
                        fullName: r.fullName,
                        position: r.position,
                        photoUrl: r.photoUrl,
                        branchIds: r.branchIds,
                        isActive: r.isActive,
                    }))}
                    branches={branchOptions}
                    getStaffSchedule={getStaffSchedule}
                    setStaffSchedule={setStaffSchedule}
                    onSavedToast={msg => showToast(msg, 'success')}
                    onErrorToast={msg => showToast(msg, 'error')}
                />
            )}

            {tab !== 'schedule' && (
            <>
            <div className={s.toolbar}>
                <div className={s.toolbarMeta}>
                    {rows && (
                        <>
                            <span className={s.count}>
                                {fill(t('ost.count.staff'), { count: filteredRows?.length ?? 0 })}
                            </span>
                            <span>
                                {fill(t('ost.count.active'), { count: activeCount })}
                                {inactiveCount > 0 ? ` · ${fill(t('ost.count.inactive'), { count: inactiveCount })}` : ''}
                                {pending.length > 0 ? ` · ${fill(t('ost.count.pending'), { count: pending.length })}` : ''}
                            </span>
                        </>
                    )}
                </div>
                {canManageStaff && (
                    <button type="button" className={s.addBtn} onClick={openAdd}>
                        {t('ost.addStaff')}
                    </button>
                )}
            </div>

            {/* AB#1904 (W-1) — Staff cap upgrade hint. Mirrors the
                BranchesSection upgrade card visual and copy convention. */}
            {atStaffCap && (
                <StaffCapUpgradeCard planName={planName} currentCap={staffCap} />
            )}

            {/* CR-2026-05-16-v1 — filter row: search + branch + service.
                Local client-side, mirrors Services-list pattern from CR-v5
                (will move to URL-persisted state alongside that work). */}
            <div className={s.filterRow}>
                <div className={s.searchWrap}>
                    <span className={s.searchIcon} aria-hidden="true">⌕</span>
                    <input
                        type="text"
                        className={s.searchInput}
                        value={filterSearch}
                        onChange={e => setFilterSearch(e.target.value)}
                        placeholder={t('ost.search.placeholder')}
                        aria-label={t('ost.search.aria')}
                    />
                </div>
                {branches.length >= 2 && (
                    <div className={s.filterChip}>
                        <span className={s.filterChipLabel}>{t('ost.filter.branch')}</span>
                        <select
                            value={filterBranchId}
                            onChange={e => setFilterBranchId(e.target.value)}
                            aria-label={t('ost.filter.branchAria')}
                        >
                            <option value="">{t('ost.filter.all')}</option>
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                {services.length > 0 && (
                    <div className={s.filterChip}>
                        <span className={s.filterChipLabel}>{t('ost.canPerform')}</span>
                        <select
                            value={filterServiceId}
                            onChange={e => setFilterServiceId(e.target.value)}
                            aria-label={t('ost.filter.serviceAria')}
                        >
                            <option value="">{t('ost.filter.any')}</option>
                            {services
                                .slice()
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map(svc => (
                                    <option key={svc.id} value={svc.id}>{svc.name}</option>
                                ))}
                        </select>
                    </div>
                )}
                {hasActiveFilters && (
                    <button type="button" className={s.clearAllLink} onClick={clearAllFilters}>
                        {t('ost.filter.clearAll')}
                    </button>
                )}
            </div>

            {fetchError && (
                <div className={s.errorBanner}>{fetchError}</div>
            )}

            {isInitialLoading && (
                <ul className={s.cardList} aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <li key={i} className={s.skeletonCard}>
                            <div className={`${s.skeletonLine} ${s.skeletonLineMed}`} />
                            <div className={`${s.skeletonLine} ${s.skeletonLineLong}`} />
                            <div className={`${s.skeletonLine} ${s.skeletonLineShort}`} />
                        </li>
                    ))}
                </ul>
            )}

            {/* CR-2026-05-16-v1 — Pending invitations group above accepted staff.
                Solves the "I sent an invitation but it doesn't appear" bug.
                Each row has Resend (re-fire email + refresh TTL), Copy link
                (paste into WhatsApp/SMS — the most reliable channel in JO/UAE
                while email DMARC/SPF isn't set up), and Revoke (hard-delete). */}
            {pending.length > 0 && (
                <>
                    <div className={s.groupHeading}>
                        {t('ost.group.pending')} <span className={s.groupCount}>({pending.length})</span>
                    </div>
                    <ul className={s.cardList}>
                        {pending.map(inv => (
                            <li key={inv.id} className={`${s.card} ${s.cardPending}`}>
                                <div className={`${s.avatar} ${s.avatarPending}`} aria-hidden="true">✉</div>
                                <div className={s.cardMain}>
                                    <div className={s.cardNameRow}>
                                        <span className={s.cardName}>{inv.fullName || inv.email}</span>
                                        <span className={`${s.pill} ${s.statusPending}`}>{t('ost.invite.pending')}</span>
                                        {inv.position && (
                                            <span className={`${s.pill} ${s.positionPill}`}>{inv.position}</span>
                                        )}
                                    </div>
                                    <div className={s.cardMeta}>
                                        <span>{inv.email}</span>
                                        {inv.phoneNumber && (
                                            <>
                                                <span className={s.cardMetaSep}>·</span>
                                                <span>{inv.phoneNumber}</span>
                                            </>
                                        )}
                                        <span className={s.cardMetaSep}>·</span>
                                        <span>{formatExpiry(inv.expiresAt, t)}</span>
                                    </div>
                                    {/* O3 (SO-15) — the tokenised invite URL is never printed;
                                        the owner copies it with this button instead. */}
                                    {inv.invitationLink && (
                                        <div className={s.inviteLinkRow}>
                                            <button
                                                type="button"
                                                className={s.copyBtn}
                                                onClick={() => handleCopyInvitationLink(inv.id, inv.invitationLink)}
                                                aria-label={fill(t('ost.invite.copyLinkFor'), { name: inv.fullName || inv.email })}
                                            >
                                                <span aria-hidden="true">{copiedInvitationId === inv.id ? '✓' : '⧉'}</span>
                                                {' '}
                                                {copiedInvitationId === inv.id ? t('ost.invite.copied') : t('ost.invite.copyLink')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className={s.cardActions}>
                                    <button
                                        type="button"
                                        className={s.iconBtn}
                                        onClick={() => handleResendInvitation(inv.id, inv.email)}
                                        disabled={busyInvitationId != null}
                                        aria-label={fill(t('ost.invite.resendTo'), { email: inv.email })}
                                    >
                                        {busyInvitationId === inv.id ? t('ost.working') : t('ost.invite.resend')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${s.iconBtn} ${s.iconBtnDanger}`}
                                        onClick={() => handleRevokeInvitation(inv.id, inv.email)}
                                        disabled={busyInvitationId != null}
                                        aria-label={fill(t('ost.invite.revokeTo'), { email: inv.email })}
                                    >
                                        {t('ost.invite.revoke')}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {showList && filteredRows && filteredRows.length === 0 && (
                <div className={s.errorBanner} style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.65)' }}>
                    {(rows?.length ?? 0) === 0
                        ? null
                        : t(hasActiveFilters ? 'ost.empty.noMatchFilters' : 'ost.empty.noMatchTab')}
                </div>
            )}

            {showList && filteredRows && filteredRows.length > 0 && (
                <>
                    {pending.length > 0 && (
                        <div className={s.groupHeading}>
                            {t('ost.group.active')} <span className={s.groupCount}>({filteredRows.length})</span>
                        </div>
                    )}
                    <ul className={s.cardList}>
                        {filteredRows.map(renderCard)}
                    </ul>
                </>
            )}
            </>
            )}

            {isModalOpen && (
                <ManageStaffModal
                    isOpen={isModalOpen}
                    isAdd={isAdd}
                    initial={editingRow ? toFormFromRow(editingRow) : null}
                    services={serviceOptions}
                    initialServiceIds={initialServiceIdsForModal}
                    branches={branchOptions}
                    initialBranchIds={initialBranchIdsForModal}
                    loading={loading || saving}
                    externalError={modalExternalError}
                    salonServiceType={salonServiceType}
                    onClose={closeModal}
                    onSave={handleSave}
                    onDelete={openDeleteFromModal}
                    // CR-2026-05-16-v1 — Photo upload only available in edit
                    // mode (need a staffId to attach to). After upload, refresh
                    // the underlying rows so the staff card reflects the new
                    // avatar without a full reload.
                    onUploadPhoto={
                        !isAdd && editingRow
                            ? async (file: File) => {
                                  const newUrl = await setStaffPhoto(editingRow.id, file);
                                  setRows(prev =>
                                      prev ? prev.map(r => r.id === editingRow.id ? { ...r, photoUrl: newUrl } : r) : prev,
                                  );
                                  return resolveStaffPhoto(newUrl) ?? newUrl;
                              }
                            : undefined
                    }
                />
            )}

            {isDeleteOpen && (
                <DeleteModal
                    isOpen={isDeleteOpen}
                    onClose={() => {
                        setIsDeleteOpen(false);
                        setDeletingRow(null);
                    }}
                    onDelete={handleDelete}
                    branchName={deletingRow ? deletingRow.fullName ?? '' : ''}
                    message={t('ost.delete.message')}
                />
            )}

            {/* AB#1862 — Booking-conflict guard confirm dialog (Set* paths).
                Sits above the staff modal (own z-index layer). On owner
                confirm, re-fires the original mutation with force=true. */}
            {confirmDialog && (
                <ConfirmBookingConflictDialog
                    isOpen={true}
                    details={confirmDialog.details}
                    staffName={confirmDialog.staffName}
                    branchNames={new Map(branches.map(b => [b.id, b.name]))}
                    serviceNames={new Map(services.map(s => [s.id, s.name]))}
                    inlineError={confirmDialog.inlineError}
                    busy={confirmDialog.busy}
                    onCancel={() => setConfirmDialog(null)}
                    onConfirmForce={handleConfirmForce}
                />
            )}

            {/* AB#1862 — HARD block banner for the Delete path. No force
                CTA; owner must cancel/reassign the bookings first. */}
            {blockBanner && (
                <BookingConflictBlockBanner
                    isOpen={true}
                    details={blockBanner.details}
                    staffName={blockBanner.staffName}
                    onClose={() => {
                        setBlockBanner(null);
                        setDeletingRow(null);
                    }}
                />
            )}
        </div>
    );
};

export default ManageStaffSection;
